import { c as _c } from "react/compiler-runtime";
import { add } from 'date-fns';
import { useField } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import styled, { css } from 'styled-components';
import { Resolution } from '../../../utils/chart';
import { formatDate } from '../../../utils/date';
import { useDatePreferences } from '../../hooks/useDatePreferences';
import Button from '../base/Button';
import { StyledCard } from '../base/Card';
import { Div } from '../helpers/StyledUtils';
import HistogramTimebar from './Timebar';
const StyledSlider = styled.div`
  user-select: none;
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
`;
const StyledSliderStep = styled.div<{
  active?: boolean;
}>(({
  theme,
  active
}) => css`
    user-select: none;
    border-bottom: 1px solid
      ${active ? theme.colors.on.neutral.primary : theme.colors.outline.neutral};
    position: relative;
    height: 2px;
  `);
const StyledSliderHandleTime = styled.div<{
  active?: boolean;
  $end: boolean;
  direction: 'left' | 'right';
}>(({
  theme,
  active,
  $end,
  direction
}) => css`
    cursor: grab;
    user-select: none;
    border: 1px solid ${active ? theme.colors.on.neutral.primary : theme.colors.outline.neutral};
    color: ${active ? theme.colors.on.neutral.primary : theme.colors.on.neutral.secondary_neutral};
    font-size: ${theme.pxToRem(12)};
    line-height: ${theme.pxToRem(18)};
    font-weight: 500;
    border-radius: ${theme.radius.small};
    background-color: ${theme.colors.surface.base.surface};
    padding: ${theme.spacing(0)} ${theme.spacing(1)};
    z-index: 1;
    width: fit-content;
    position: absolute;
    top: 14px;
    white-space: nowrap;
    ${direction === 'left' ? $end ? css`
            transform: translate(100%);
          ` : css`
            transform: translate(-100%);
          ` : $end ? css`
            right: 0;
          ` : css`
            left: 0;
          `}
  `);
const StyledSliderHandle = styled.div<{
  $end?: boolean;
  active?: boolean;
}>(({
  theme,
  $end,
  active
}) => css`
    ${!active && css`
      display: none;
    `}
    user-select: none;
    position: absolute;
    cursor: grab;
    height: 12px;
    width: 12px;
    border-radius: 50%;
    z-index: 1;
    background-color: ${theme.colors.surface.base.surface};
    border: 1px solid ${theme.colors.on.neutral.primary};

    ${$end ? css`
          right: 0;
        ` : css`
          left: 0;
        `}
    bottom: -8px;
  `);
const StyledSaveCard = styled(StyledCard)(({
  theme
}) => css`
    position: absolute;
    left: 0;
    top: -60px;
    width: fit-content;
    z-index: 12;
    box-shadow: ${theme.elevation[3]};
    border-radius: 8px;
  `);
const granularity_formating = {
  second: 'MMM d, h:mm:ss a',
  minute: 'MMM d, h:mm a',
  hour: 'MMM d, h:mm a',
  day: 'MMM d, h:mm a'
};
interface HistogramRangePickerProps {
  chart_resolution: Resolution;
  axis_resolution: Resolution;
  start_date: Date;
  show_controls?: boolean;
  children: React.ReactNode;
}
const HistogramRangePicker: React.FC<HistogramRangePickerProps> = t0 => {
  const $ = _c(20);
  const {
    chart_resolution,
    axis_resolution,
    start_date,
    show_controls: t1,
    children
  } = t0;
  const show_controls = t1 === undefined ? true : t1;
  const {
    timezone
  } = useDatePreferences();
  const [t2,, t3] = useField("timebar_start");
  const {
    value: timebar_start
  } = t2;
  const {
    setValue: setTimebarStart
  } = t3;
  const [t4,, t5] = useField("timebar_end");
  const {
    value: timebar_end
  } = t4;
  const {
    setValue: setTimebarEnd
  } = t5;
  const ref = useRef(null);
  const drag_event = useRef(null);
  const [dragging, setDragging] = useState(null);
  const apply_button_ref = useRef(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = document.querySelector("#main");
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const element = t6;
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      name: "preventOverflow",
      options: {
        boundary: element as any,
        mainAxis: true,
        altAxis: false,
        padding: 8
      }
    };
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      placement: "bottom",
      modifiers: [t7, {
        name: "offset",
        options: {
          offset: [0, -42]
        }
      }]
    };
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const {
    styles,
    attributes
  } = usePopper(referenceElement, popperElement, t8);
  const onMove = event => {
    if (!drag_event.current) {
      return;
    }
    const {
      rect_left,
      from,
      step_width,
      start_step
    } = drag_event.current;
    const raw = (event.clientX - rect_left) / step_width;
    const current_step = Math.min(Math.max(from === "end" ? Math.ceil(raw) - 1 : Math.floor(raw), 0), chart_resolution.steps - 1);
    if (from) {
      if (from === "end") {
        if (current_step >= timebar_start) {
          setTimebarEnd(current_step);
        }
      } else {
        if (current_step <= timebar_end) {
          setTimebarStart(current_step);
        }
      }
    } else {
      const direction = current_step > start_step ? "right" : "left";
      if (current_step !== start_step) {
        if (direction === "right") {
          if (timebar_start !== start_step) {
            setTimebarStart(start_step);
          }
          if (timebar_end !== current_step) {
            setTimebarEnd(current_step);
          }
        } else {
          if (timebar_start !== current_step) {
            setTimebarStart(current_step);
          }
          if (timebar_end !== start_step) {
            setTimebarEnd(start_step);
          }
        }
      }
    }
  };
  const onStart = (event_0, from_0) => {
    const rect = ref.current.getBoundingClientRect();
    const step_width_0 = ref.current.offsetWidth / chart_resolution.steps;
    const start_step_0 = from_0 ? from_0 === "start" ? timebar_start : timebar_end : Math.min(Math.max(Math.floor((event_0.clientX - rect.left) / step_width_0), 0), chart_resolution.steps - 1);
    drag_event.current = {
      from: from_0 ? from_0 : null,
      rect_left: rect.left,
      start_step: start_step_0,
      step_width: step_width_0
    };
    setDragging(from_0 ?? null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
  };
  const onEnd = event_1 => {
    if (!drag_event.current) {
      return;
    }
    if (event_1) {
      const round = drag_event.current.from === "end" ? Math.ceil : Math.floor;
      const current_step_0 = Math.min(Math.max(round((event_1.clientX - drag_event.current.rect_left) / drag_event.current.step_width), 0), chart_resolution.steps - 1);
      if (current_step_0 === drag_event.current.start_step) {
        setTimebarStart(drag_event.current.start_step);
        setTimebarEnd(drag_event.current.start_step);
      }
    }
    if (apply_button_ref.current) {
      apply_button_ref.current.focus();
    }
    setDragging(null);
    drag_event.current = null;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onEnd);
  };
  let t10;
  let t9;
  if ($[3] !== dragging) {
    t9 = () => {
      if (!dragging) {
        document.body.style.cursor = "unset";
      }
    };
    t10 = [dragging];
    $[3] = dragging;
    $[4] = t10;
    $[5] = t9;
  } else {
    t10 = $[4];
    t9 = $[5];
  }
  useEffect(t9, t10);
  const curriedOnStart = from_1 => event_2 => onStart(event_2, from_1);
  const range_selected = timebar_start !== 0 || timebar_end !== chart_resolution.steps - 1;
  const start_label_direction = timebar_start > 10 ? "left" : "right";
  const end_label_direction = timebar_end > chart_resolution.steps - 10 ? "right" : "left";
  const midpoint_step = Math.floor((timebar_start + timebar_end) / 2);
  if (!show_controls) {
    let t11;
    if ($[6] !== children) {
      t11 = <>{children}</>;
      $[6] = children;
      $[7] = t11;
    } else {
      t11 = $[7];
    }
    return t11;
  }
  let t11;
  if ($[8] !== dragging) {
    t11 = dragging && <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 999999,
      cursor: dragging ? "grabbing" : "ew-resize"
    }} />;
    $[8] = dragging;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const t12 = range_selected && <StyledSaveCard ref={setPopperElement} style={styles.popper} {...attributes.popper} p={0.5} raised={true}><Button ghost={true} onClick={() => {
      onEnd();
      setTimebarStart(0);
      setTimebarEnd(chart_resolution.steps - 1);
    }}>Cancel</Button><Button ghost={true} primary={true} submit={true} ref={apply_button_ref}>Apply</Button></StyledSaveCard>;
  const T0 = StyledSlider;
  const t13 = Array.from(Array(chart_resolution.steps).keys()).map(i => <StyledSliderStep active={i >= timebar_start && i <= timebar_end && range_selected} key={i}>{(timebar_start === i || timebar_end === i) && <>{timebar_start === i && <div onMouseDown={curriedOnStart("start")}><StyledSliderHandleTime direction={start_label_direction} $end={false} active={range_selected}>{formatDate(add(start_date, {
            seconds: i * chart_resolution.unit * chart_resolution.factor_to_seconds
          }), granularity_formating[chart_resolution.granularity], timezone)}</StyledSliderHandleTime><StyledSliderHandle onMouseDown={curriedOnStart("start")} $end={false} active={range_selected} /></div>}{timebar_end === i && <div onMouseDown={curriedOnStart("end")}><StyledSliderHandleTime direction={end_label_direction} $end={true} active={range_selected}>{formatDate(add(start_date, {
            seconds: (i + 1) * chart_resolution.unit * chart_resolution.factor_to_seconds
          }), granularity_formating[chart_resolution.granularity], timezone)}</StyledSliderHandleTime><StyledSliderHandle onMouseDown={curriedOnStart("end")} $end={true} active={range_selected} /></div>}</>}{range_selected && midpoint_step === i && <div ref={setReferenceElement} />}</StyledSliderStep>);
  let t14;
  if ($[10] !== T0 || $[11] !== t13) {
    t14 = <T0>{t13}</T0>;
    $[10] = T0;
    $[11] = t13;
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  let t15;
  if ($[13] !== axis_resolution || $[14] !== start_date) {
    t15 = <Div><HistogramTimebar start_date={start_date} resolution={axis_resolution} /></Div>;
    $[13] = axis_resolution;
    $[14] = start_date;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== t12 || $[17] !== t14 || $[18] !== t15) {
    t16 = <>{t12}{t14}{t15}</>;
    $[16] = t12;
    $[17] = t14;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  return <>{t11}<div ref={ref} onMouseDown={onStart}>{children}</div>{t16}</>;
};
export default HistogramRangePicker;